<script setup>
import { ref, computed, onMounted } from 'vue';
import TimeSavedAsTextMessage from "../../time-saved/asTextMessage.vue";

const loading = ref(true);
const data = ref(null);
const expanded = ref(false);

const specialistMeta = {
    stella: { bgClass: 'bg-aiAgentWebDesigner/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentWebDesigner' },
    susan: { bgClass: 'bg-aiAgentDataAnalyst/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentDataAnalyst' },
    tom: { bgClass: 'bg-aiAgentRelationshipManager/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentRelationshipManager' },
    maya: { bgClass: 'bg-aiAgentContentCreator/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentContentCreator' },
    alex: { bgClass: 'bg-aiAgentNewsletterSpecialist/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentNewsletterSpecialist' },
    nina: { bgClass: 'bg-aiAgentSalesAssistant/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentSalesAssistant' },
    oscar: { bgClass: 'bg-aiAgentLeadFinder/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentLeadFinder' },
    leo: { bgClass: 'bg-aiAgentAutomationExpert/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentAutomationExpert' },
    flowie: { bgClass: 'bg-gray-100', iconColor: 'text-gray-600', barColor: 'bg-gray-400' },
    paige: { bgClass: 'bg-aiAgentWebDesigner/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentWebDesigner' },
    pulse: { bgClass: 'bg-aiAgentDataAnalyst/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentDataAnalyst' },
    buddy: { bgClass: 'bg-aiAgentRelationshipManager/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentRelationshipManager' },
    spark: { bgClass: 'bg-aiAgentContentCreator/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentContentCreator' },
    reach: { bgClass: 'bg-aiAgentNewsletterSpecialist/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentNewsletterSpecialist' },
    closer: { bgClass: 'bg-aiAgentSalesAssistant/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentSalesAssistant' },
    scout: { bgClass: 'bg-aiAgentLeadFinder/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentLeadFinder' },
    loop: { bgClass: 'bg-aiAgentAutomationExpert/60', iconColor: 'text-gray-800', barColor: 'bg-aiAgentAutomationExpert' },
};

function specialistName(key) {
    if (key === 'flowie') return _mft('dashboard:flowie.name');
    return _mft('dashboard:aiSpecialists.' + key + '.name');
}

function formatHours(h) {
    if (!h) return '0';
    return h % 1 === 0 ? Math.round(h) : h;
}

const displayHours = computed(() => formatHours(data.value?.hours_saved));
const displayMonthlyAvg = computed(() => formatHours(data.value?.monthly_avg_hours));

const maxHoursSaved = computed(() => {
    if (!data.value?.by_specialist) return 1;
    return Math.max(1, ...Object.values(data.value.by_specialist).map(s => s.hours_saved));
});

async function fetchSummary() {
    try {
        const response = await axios.get('/bo/ai-weekly-summary');
        data.value = response.data;
    } catch (e) {
        // Silently fail
    } finally {
        loading.value = false;
    }
}

onMounted(() => {
    fetchSummary();
});
</script>

<template>
    <div>
        <!-- Loading skeleton -->
        <div v-if="loading" class="mb-4 p-5 bg-white rounded-wl animate-pulse">
            <div class="w-48 h-4 mb-3 bg-gray-200 rounded"></div>
            <div class="w-32 h-3 bg-gray-200 rounded"></div>
        </div>

        <card-basic
            v-else-if="data && displayHours > 1"
            :compact="true"
            class="mb-4"
        >
            <div class="p-5">
                <div class="pb-4 border-b border-gray-100">
                    <div>
                        <div class="flex items-baseline gap-1">
                            <span class="text-lg font-bold text-gray-900">
                                {{ displayHours }} {{ _mft('dashboard:weeklySummary.hours') }}
                            </span>
                            {{ _mft('dashboard:weeklySummary.savedThisWeek') }}
                        </div>
                    </div>

                    <time-saved-as-text-message class="text-gray-400 text-xs" :hours-saved="displayHours" />
                </div>

                <!-- Top row: metrics -->
                <div class="grid grid-cols-4 gap-4 mt-3">
                    <!-- Monthly average -->
                    <div v-if="data.monthly_avg_hours > 0" class="hidden sm:block">
                        <div class="flex items-baseline gap-1">
                            <span class="font-bold text-gray-900">{{ displayMonthlyAvg }}</span>
                            <span class="text-sm text-gray-500">{{ _mft('dashboard:weeklySummary.hoursPerMonth') }}</span>
                        </div>
                        <p class="text-xs text-gray-400">{{ _mft('dashboard:weeklySummary.monthlyAverage') }}</p>
                    </div>

                    <!-- Tasks count -->
                    <div
                        class="hidden sm:block cursor-pointer"
                        @click="expanded = !expanded"
                    >
                        <div class="flex items-baseline gap-1">
                            <span class="font-bold text-gray-900">{{ data.week_requests }}</span>
                            <span class="text-sm text-gray-500">{{ _mft('dashboard:weeklySummary.tasks') }}</span>
                        </div>
                        <p class="text-xs text-gray-400">
                            {{ _mft('dashboard:weeklySummary.across') }} {{ data.specialist_count }} {{ _mft('dashboard:weeklySummary.specialists') }}
                        </p>
                    </div>

                    <div class="col-span-2">
                        <div v-if="Object.keys(data.by_specialist).length > 0" class="flex flex-wrap items-center gap-2">
                            <div
                                v-for="(stats, key) in data.by_specialist"
                                :key="key"
                                class="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full"
                                :class="specialistMeta[key]?.bgClass || 'bg-gray-100'"
                            >
                                <span class="font-medium" :class="specialistMeta[key]?.iconColor || 'text-gray-600'">
                                    {{ specialistName(key) }}
                                </span>
                                        <span class="text-gray-500">
                                    {{ stats.requests }} {{ stats.requests === 1 ? _mft('dashboard:weeklySummary.task') : _mft('dashboard:weeklySummary.tasks') }}
                                </span>
                            </div>

                            <!-- Expand toggle -->
                            <button-base
                                style-type="default"
                                size="sm"
                                class="gap-1 !rounded-full"
                                @click="expanded = !expanded"
                            >
                                {{ expanded ? _mft('dashboard:weeklySummary.hideDetails') : _mft('dashboard:weeklySummary.showDetails') }}
                                <template #icon>
                                    <svg
                                        class="order-last w-3 h-3 ml-1 transition-transform duration-200"
                                        :class="{ 'rotate-180': expanded }"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </template>
                            </button-base>
                        </div>

                    </div>
                </div>

                <!-- Empty state -->
                <div v-if="data.week_requests === 0" class="flex items-center gap-2 mt-3">
                    <p class="text-sm text-gray-400">{{ _mft('dashboard:weeklySummary.noActivity') }}</p>
                </div>
            </div>

            <!-- Folddown: per-specialist time estimates -->
            <transition
                enter-active-class="transition-all duration-200 ease-out"
                enter-from-class="opacity-0 -translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-1"
            >
                <div v-if="expanded && Object.keys(data.by_specialist).length > 0" class="px-5 pb-4">
                    <div class="pt-3 border-t border-gray-100">
                        <p class="mb-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
                            {{ _mft('dashboard:weeklySummary.estimatedTimeSaved') }}
                        </p>
                        <div class="space-y-2">
                            <div
                                v-for="(stats, key) in data.by_specialist"
                                :key="key"
                                class="flex items-center gap-3"
                            >
                                <span class="flex-shrink-0 w-16 text-xs font-medium text-gray-700 truncate" :title="specialistName(key)">
                                    {{ specialistName(key) }}
                                </span>
                                <div class="flex-1 min-w-0 h-2 overflow-hidden rounded-full">
                                    <div
                                        class="h-full rounded-full transition-all duration-300"
                                        :class="specialistMeta[key]?.barColor || 'bg-gray-400'"
                                        :style="{ width: (stats.hours_saved / maxHoursSaved * 100) + '%' }"
                                    ></div>
                                </div>
                                <span class="flex-shrink-0 text-xs text-gray-500 whitespace-nowrap text-right">
                                    {{ formatHours(stats.hours_saved) }}h
                                    <span class="text-gray-300">&middot;</span>
                                    {{ stats.minutes_per_task }}{{ _mft('dashboard:weeklySummary.minPerTask') }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </card-basic>
    </div>
</template>
